/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */
/**
 * created by cmiller
 * boxPlotElement.js -- constructors for Dyn.BoxPlotElement() and derived objects BoxOnlyPlotElement, BoxAndOutlierPlotElement, BoxAndDotPlotElement.
 */

import { DotPlotBase } from './dotPlotElement';
export const wrapDynBoxPlotElement = function(Dyn) {

/**
 * Dyn.BoxPlotElement()
 */
class BoxPlotElement extends DotPlotBase {

    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityShape,
            replaceType: Dyn.EPlotControl.kControlsShape,
            primaryType: Dyn.EPlotControl.kControlsBoxPlot,
            otherControls: [
                Dyn.EPlotControl.kControlsCaseOrder ]
        }, Dyn );

        // limit case stacking area to within the box (1==entire cell, 0.5=half of cell height, etc.)
        this.stackHeight = Dyn.plotUtil.boxHeight;
        this.showOutliers = false;
        this.showDots = false;
        //this.needsDefaultTitle = true;
    }

    get kDefaultTitle() {
        return Dyn.plotTitles.BOX_PLOT;
    }

    get kUndoAction() {
        return Dyn.plotTitles.BOX_PLOT;
    }

    calcNumIconsPerCase( ioWhatChanged, subViewRef ) {
        var xAxisView = subViewRef.xAxisView,
            yAxisView = subViewRef.yAxisView,
            iconsPerCase = 1; // default: don't allow muliple icons per case

        // determine axis where we place boxplot(s), if any
        // CAUTION: number of active attributes has not been set yet, use isNumericActiveOrInactive(), not isNumericAxis().
        // axis type on X and Y will be set by calcAxis().  #1434
        this.isBoxOnX = xAxisView.isNumericActiveOrInactive();
        this.isBoxOnY = this.isBoxOnX ? false : yAxisView.isNumericActiveOrInactive();

        // determine if we use color in the boxes
        let coloringAttribute = this.plotShaper.dataSet.getSelectedAttribute(),
            isMultiCategoryColoringAttribute = (this.getNumCategoriesInLegend( coloringAttribute ) > 1),
            crossAxis = (this.isBoxOnX ? yAxisView : (this.isBoxOnY ? xAxisView : null)),
            categoricalAttribute = (crossAxis && crossAxis.isCategoricalActiveOrInactive()) ? crossAxis.getAttribute() : null;
        this.isColoredBoxPlotFill = (isMultiCategoryColoringAttribute && (coloringAttribute === categoricalAttribute) /*&& ! this.showDots*/);

        // determine the number attributes used, for this.calcMissingValueCases()
        this.numAttributesUsedX = Math.min( 1, xAxisView.allAttributes.count() );
        this.numAttributesUsedY = Math.min( 1, yAxisView.allAttributes.count() );
        if( this.isBoxOnX && subViewRef.yAxisView.isNumericActiveOrInactive() ) {
            // in the case of a scatterplot (X and Y numeric attribute), suppress Y axis
            this.numAttributesUsedY = 0;
        } else if( xAxisView.isCategoricalActiveOrInactive() && yAxisView.isCategoricalActiveOrInactive() ) {
            // if categorical attributes on both X and Y, suppress X axis
            this.numAttributesUsedX = 0;
        }

        return iconsPerCase; // disallow multiple icons per case for boxplots, unlike dot/scatter/line plot
    }

    /** Update the plot's axis model, removing any elements incompatible with this PlotElement. */
    calcAxis( ioWhatChanged, subViewRef ) {
        "use strict";
        var xAxisView = subViewRef.xAxisView,
            yAxisView = subViewRef.yAxisView,
            topMargin = subViewRef.topMargin,
            rightMargin = subViewRef.rightMargin;

        // update default titles once on plot creation, then let user customize (don't change on recalc)
        this.plotShaper.setDefaultPlotTitle( subViewRef.topMargin, Dyn.loc( this.kDefaultTitle ) );

        // update axis prompt, if any, and "incomplete axis" styling if applicable.
        let isIncompletePlot = Boolean( !(xAxisView.isNumericActiveOrInactive() || yAxisView.isNumericActiveOrInactive()) ),
            wantIncompleteStyleOnY = Boolean( isIncompletePlot && xAxisView.isCategoricalActiveOrInactive() ),
            wantIncompleteStyleOnX = Boolean( isIncompletePlot && !wantIncompleteStyleOnY ),
            wantDisabledLegend = isIncompletePlot || !(this.showOutliers || this.showDots || this.isColoredBoxPlotFill);
        setAxisPrompt( xAxisView, wantIncompleteStyleOnX, yAxisView );
        setAxisPrompt( yAxisView, wantIncompleteStyleOnY, xAxisView );

        // update axis settings to disable/enable attributes
        xAxisView.setPlotSpecificOptions( {
            numActiveAttributes: this.numAttributesUsedX,
            isIncompleteAxisStyle: wantIncompleteStyleOnX
        } );
        yAxisView.setPlotSpecificOptions( {
            numActiveAttributes: this.numAttributesUsedY,
            isIncompleteAxisStyle: wantIncompleteStyleOnY
        } );
        topMargin.setPlotSpecificOptions( null );
        rightMargin.setPlotSpecificOptions( { disableAttribute: wantDisabledLegend } );

        this.wantStackUp = this.calcStackDirection( xAxisView, yAxisView ); // for when 0 or 1 attribute present

        function setAxisPrompt( axisView, isIncompleteStyle, crossAxisView ) {
            // note: was Dyn.axisUtil.setTitleForNumeric1DPlot(), moved here because only boxplot uses it --CDM 2022-09-19
            if( axisView.isEmptyOfAttributes() && (isIncompleteStyle || crossAxisView.isCategoricalActiveOrInactive()) ) {
                // prompt for required numeric value attribute, or if we already have a category on the cross-axis
                axisView.setTitle( Dyn.loc( Dyn.plotStrings.promptValue ) );
            } else if( axisView.isPromptAxis() ) {
                // update axis prompt if there is one
                if( crossAxisView.isNumericActiveOrInactive() ) {
                    // prompt for optional categorical attribute on non-numeric axis
                    axisView.setTitle( Dyn.loc( Dyn.plotStrings.promptGroup ) );
                } else {
                    // else prompt for numeric values on X, category groupings on Y (because we assume horizontal boxplots are standard)
                    axisView.setTitle( Dyn.loc( axisView.isHorizontal ? Dyn.plotStrings.promptValue : Dyn.plotStrings.promptGroup ) );
                }
            } // else leave axis as-is (e.g. blank or with attribute names)
        }
    }

    /** Change the icon style to circles, if needed, and reduce the circle size when lines are showing.  */
    calcIconStyle( ioPositions, xAxisView, yAxisView ) {
        this.plotShaper.iconStyler.setStyleCircleOrCustomIcon( ioPositions.length, Dyn.plotUtil.circleWithLinesSizeFraction );
    }

    /**
     * Position cases and boxes and outliers.
     * NOTE: because deep inheritance does not work correctly, we make this a separate function with unique name,
     * instead of just calcPosition().
     * @param ioPositions
     * @param xAxisView
     * @param yAxisView
     * @param ioAdornments
     */
    calcAllBoxPositions( ioPositions, xAxisView, yAxisView, ioAdornments ) {
        // else position dots along numeric axis
        var iconStyler = this.plotShaper.iconStyler,
            dataSet = this.plotShaper.dataSet,
            xAxisType = xAxisView.type,
            yAxisType = yAxisView.type,
            xIsSeparated = xAxisView.isCategoricalActiveAxis(),
            yIsSeparated = yAxisView.isCategoricalActiveAxis(),
            xDotOffset = (this.isBoxOnY && xAxisType !== 2) ? 0.5 : 0, // to align dots with Y boxplot axis if no categories on X
            yDotOffset = (this.isBoxOnX && yAxisType !== 2) ? 0.5 : 0, // to align dots with X boxplot axis if no categories on Y
            sortingAttribute = dataSet.getOrderByAttribute(),
            numCasesInDataSet = this.plotShaper.numCasesInDataSet,
            radius = (this.showDots || this.showOutliers) ? this.plotShaper.iconStyler.radius : 0,
            i, j = ioPositions.length, p;

        // set case position for null, categorical, or numeric axis on x and y
        // set radius to zero or not to hide in place for good animations
        for( i = 0; i < j; i++ ) {
            p = ioPositions[i];
            p.caseIndex = i % numCasesInDataSet;
            p.xAttrIndex = ((this.numAttributesUsedX < 2) ? 0 : Dyn.mathUtil.floor( i / numCasesInDataSet ));
            p.yAttrIndex = ((this.numAttributesUsedY < 2) ? 0 : Dyn.mathUtil.floor( i / numCasesInDataSet ));
            p.x = xAxisView.getCaseDataAsNumber( p.caseIndex, p.xAttrIndex );
            p.y = yAxisView.getCaseDataAsNumber( p.caseIndex, p.yAttrIndex );
            p.isVisible = dataSet.isCaseNonMissingNonFiltered( p.caseIndex );
            this.calcBinIndex( p, xIsSeparated, yIsSeparated );
            // convert from data coordinates (numeric value, category index, etc) to view coordinates (pixel position)
            p.x = xAxisView.dataToPixel( p.x + xDotOffset );
            p.y = yAxisView.dataToPixel( p.y + yDotOffset );
            p.r = radius; // initial radius, until we know where outliers are (if using outliers)
            setPositionIfMissingOrFiltered( p ); // position all invisible cases offscreen
        }

        this.populateCellsWithCases( ioPositions );		// now we can map cells to cases
        this.calcAllBoxData( xAxisView, yAxisView );	// then we can calc box data in cells

        // then hide non-outliers or all cases if box with no outliers
        if( this.showOutliers ) {
            // for BoxAndOutlierPlotElement
            // hide non-missing, non-outlier cases inside the whiskers (in place, instead of moving to missing value area, for correct initial animation position when plot changes)
            var valueAxis = (this.isBoxOnX ? xAxisView : (this.isBoxOnY ? yAxisView : null)),
                valueAttribute = valueAxis ? valueAxis.getAttribute() : null, // multi-attribute not allowed
                plotCells = this.plotShaper.bins.cells,
                cell, value;
            // for each icon
            for( i = 0; i < j; i++ ) {
                p = ioPositions[i];
                cell = plotCells[p.cellIndex];
                value = valueAttribute.cases[p.caseIndex];
                if( p.isVisible && value >= cell.boxData.whisker1 && value <= cell.boxData.whisker2 ) {
                    setPositionHiddenInPlace( p );
                }
            }
        } else if( !this.showDots ) {
            // for BoxOnlyPlotElement
            // hide non-missing cases along the boxplot axis (in place, instead of moving to missing value area, for correct initial animation position when plot changes)
            for( i = 0; i < j; i++ ) {
                p = ioPositions[i];
                if( p.isVisible ) {
                    setPositionHiddenInPlace( p );
                }
            }
        } else {
            // stack cases in cell, if needed for the particular combination of x and y axis.
            if( xAxisType === 1 && yAxisType === 1 ) {
                // numeric scatterplot, we are done
            } else if( xAxisType === 1 || yAxisType === 1 ) { // stack to left-to-right or bottom-up against numeric axis
                this.stackInPlace( ioPositions, xAxisView, yAxisView, this.wantStackUp, sortingAttribute );
            } else {
                this.stackInBins( ioPositions, xAxisView, yAxisView, this.wantStackUp, sortingAttribute );
            }
        }

        /**
         * set the position of hidden cases to offscreen (to be animated on screen when status changes)
         * @param p {isVisible,x,y,r} -- case icon position parameters
         */
        function setPositionIfMissingOrFiltered( p ) {
            if( !(p.isVisible) ) {
                p.x = iconStyler.missingValuePosition.x;
                p.y = iconStyler.missingValuePosition.y;
                p.r = Dyn.plotUtil.missingCaseSize;
            }
        }

        /** set the status of case as invisible but in the right place on axis, for later animation.  */
        function setPositionHiddenInPlace( p ) {
            p.isVisible = false; // mark icon as invisible (to optimize icons)
            p.r = Dyn.plotUtil.missingCaseSize; // change radius to zero to hide in place
        }
    }

    /**
     *
     * @param ioPositions
     * @param xAxisView
     * @param yAxisView
     * @param ioAdornments
     */
    calcAllBoxData( xAxisView, yAxisView ) {
        // for each cell, add or modify a path string
        var plotCells = this.plotShaper.bins.cells,
            numCells = plotCells.length,
            valueAxis = (this.isBoxOnX ? xAxisView : (this.isBoxOnY ? yAxisView : null)),
            valueAttribute = valueAxis ? valueAxis.getAttribute() : null, // multi-attribute not allowed
            numBoxes = (valueAxis ? numCells : 0),
            cellIndex, cell;
        if( numBoxes === 0 )
            return;
        Dyn.assert( valueAxis );
        for( cellIndex = 0; cellIndex < numCells; cellIndex++ ) {
            // compute box data for numeric values in the cell
            cell = plotCells[cellIndex];
            cell.boxData = this.calcBoxPlotData( valueAttribute, cell.cases );
        }
    }

    /**
     *
     * @param attribute {Dyn.dataUtil.Attribute}
     * @param caseIndicesInCell
     * @returns {median,q1,q2,min,max}
     */
    calcBoxPlotData( attribute, caseIndicesInCell ) {
        let cellValues = [],
            allCaseValues = attribute.cases;

        for( let i = 0, j = caseIndicesInCell.length; i < j; i++ ) {
            let v = allCaseValues[caseIndicesInCell[i]];
            Dyn.assertArrayIndex( allCaseValues, caseIndicesInCell[i] );
            if( Dyn.dataUtil.isNumber( v ) ) {
                cellValues.push( v );
            }
        }
        let calcWhiskers = Boolean( this[Dyn.EPlotControl.kControlsBoxAndOutlierPlot] ),
            boxData = Dyn.mathUtil.quartilesOfNumericArray( cellValues, calcWhiskers );
        Object.keys( boxData ).forEach( key => { // #1664 fix precision
            boxData[key] = Dyn.mathUtil.fixPrecision( boxData[key] );
        } );
        return boxData;
    }

    /**
     * Update the adornments array for the box plot element;
     *  we make one path adornment per box, and use an empty path if no box for a particular cell.
     */
    calcAdornments( ioAdornArray, casePositions, xAxisView, yAxisView, noAnimation ) {
        var thisBox = this,
            plotMeasures = this.plotShaper.measures,
            measuresArray = plotMeasures.getMeasuresArray( this.primaryType ),
            valueAxis = (this.isBoxOnX ? xAxisView : (this.isBoxOnY ? yAxisView : null)),
            nonValueAxis = (this.isBoxOnX ? yAxisView : (this.isBoxOnY ? xAxisView : null)),
            plotCells = this.plotShaper.bins.cells,
            numCells = plotCells.length,
            numBoxes = (valueAxis ? numCells : 0),
            clipRectString = Dyn.plotView.getPlotInteriorClipRect(),
            animationTime = (noAnimation ? Dyn.plotUtil.noAnimationMs : Dyn.plotUtil.stdAnimationTimeMs),
            cellIndex /*, cell, pathString*/;

        // remove any extra boxes from previous plot
        if( ioAdornArray.length > numBoxes ) {
            for( cellIndex = ioAdornArray.length - 1; cellIndex >= numBoxes; cellIndex -= 1 ) {
                console.log( 'remove box ' + cellIndex );
                ioAdornArray[cellIndex].remove();
            }
            ioAdornArray.length = numBoxes;
        }
        measuresArray.length = 0; // remove old measures (values for summary table)
        if( numBoxes === 0 )
            return;

        // for each cell, add or modify a path string
        Dyn.assert( valueAxis );
        var attribute = valueAxis.getAttribute(),
            nonValueAttribute = nonValueAxis?.getAttribute();
        for( cellIndex = 0; cellIndex < numCells; cellIndex++ ) {

            // get the box data for numeric values in the cell
            let cell = plotCells[cellIndex],
                pathString = this.calcBoxPathStringForCell( valueAxis, cell.boxData, cell ),
                categoryIndex = this.isBoxOnX ? cell.yBinIndex : cell.xBinIndex,
                colorAttribute = this.plotShaper.dataSet.getSelectedAttribute(),
                fillColor = this.getBoxFillColor( colorAttribute, categoryIndex );

            // add any new boxes needed
            Dyn.assert( ioAdornArray.length <= numCells, "expecting one or zero path strings per cell" );
            if( !ioAdornArray[cellIndex] ) {	// add
                let pathElement = this.plotShaper.adornments.paper.path( this.plotShaper.iconStyler.calcEmptyPath() );
                pathElement.attr( {
                    'stroke-width': Dyn.plotUtil.boxPlotWidth,
                    'stroke': Dyn.plotUtil.boxPlotStroke,
                    'fill': fillColor,
                    'fill-opacity': Dyn.plotUtil.boxPlotFillOpacity,
                    'opacity': 0
                } );
                // To make the path element focusable
                pathElement.node.setAttribute( "focusable", "true" );
                pathElement.node.setAttribute( "tabindex", "0" );
                pathElement.node.setAttribute( "role", "button" );

                // Create ARIA live region for announcements (Safari only - Chrome doesn't support it with VoiceOver)
                if( Dyn.plotUtil.isSafari() ) {
                    // Note: Live region must exist in DOM before screen reader starts monitoring it
                    let liveRegion = document.getElementById( 'boxplot-live-region' );
                    if( !liveRegion ) {
                        liveRegion = document.createElement( 'div' );
                        liveRegion.id = 'boxplot-live-region';
                        liveRegion.className = 'visually-hidden';
                        // Use 'assertive' to interrupt current speech and announce immediately
                        liveRegion.setAttribute( 'aria-live', 'assertive' );
                        liveRegion.setAttribute( 'aria-atomic', 'true' );
                        // Add empty text content so screen readers start monitoring it
                        liveRegion.textContent = '\u00A0'; // non-breaking space
                        document.body.appendChild( liveRegion );
                        // Clear the non-breaking space after a moment so it's ready for real content
                        setTimeout( function() {
                            liveRegion.textContent = '';
                        }, 100 );
                    }
                }

                // Add keyboard interaction for accessibility
                pathElement.node.addEventListener( 'keydown', function( event ) {
                    if( event.key === 'Enter' || event.key === ' ' ) {
                        event.preventDefault();

                        // Show detailed statistics when Enter or Space is pressed
                        const detailedStats = pathElement.data( 'detailedStats' );
                        const screenReaderStats = pathElement.data( 'screenReaderStats' );
                        const isSafariBrowser = pathElement.data( 'isSafari' );

                        if( detailedStats ) {
                            // Show visual tooltip (pass Raphael element, not DOM node)
                            Dyn.toolTip.show( pathElement, event, {
                                x: pathElement.attrs.x,
                                y: pathElement.attrs.y
                            } );
                        }

                        // Only use live region in Safari (Chrome doesn't support it with VoiceOver)
                        if( isSafariBrowser && screenReaderStats ) {
                            const liveRegionElement = document.getElementById( 'boxplot-live-region' );

                            if( liveRegionElement ) {
                                // Double-update technique: Some screen readers need the clear/set pattern
                                // First, clear completely
                                liveRegionElement.textContent = '';

                                // Then wait longer (200ms) for screen reader to register the change
                                setTimeout( function() {
                                    liveRegionElement.textContent = screenReaderStats;
                                }, 200 );
                            }
                        }
                    } else if( event.key === 'Escape' ) {
                        // Hide tooltip on Escape
                        if( Dyn.toolTip.isMyElement( pathElement ) ) {
                            Dyn.toolTip.hide();
                        }
                        // Clear live region
                        const liveRegionElement = document.getElementById( 'boxplot-live-region' );
                        if( liveRegionElement ) {
                            liveRegionElement.textContent = '';
                        }
                    }
                } );

                ioAdornArray.push( pathElement );
            }

            // update the summary measures and tooltip string, if we have valid data
            const mv = Dyn.dataUtil.kMissingValue;
            if( Number.isFinite( cell.boxData.median )) {
                // get bare numeric values as strings
                var boxData = cell.boxData,
                    precision = this.getPrecision( attribute ), // calc significant figures
                    boxQ1 = attribute.axisValueToString( boxData.q1, precision ),
                    boxQ3 = attribute.axisValueToString( boxData.q3, precision ),
                    boxMedian = attribute.axisValueToString( boxData.median, precision ),
                    boxIQR = attribute.axisValueToStringRelativeUnits( boxData.IQR, precision ),
                    boxW1 = attribute.axisValueToString( this.showOutliers ? boxData.whisker1 : mv, precision ),
                    boxW2 = attribute.axisValueToString( this.showOutliers ? boxData.whisker2 : mv, precision ),
                    boxMin = attribute.axisValueToString( boxData.min, precision ),
                    boxMax = attribute.axisValueToString( boxData.max, precision ),
                    toolTipString,
                    attributeNames = [ attribute.name ];
                // if box plot by category, add the category name to the tooltip, ignore undefined values in the array
                if( this.isBoxOnX ) {
                    if( nonValueAttribute?.stats?.categories[cell.yBinIndex] ) {
                        attributeNames.push( nonValueAttribute?.stats?.categories[cell.yBinIndex] );
                    }
                } else {
                    if( nonValueAttribute?.stats?.categories[cell.xBinIndex] ) {
                        attributeNames.push( nonValueAttribute?.stats?.categories[cell.xBinIndex] );
                    }
                }
                toolTipString = boxToolTipString( boxData, attribute, this.showOutliers, attributeNames );
                const screenReaderAnnouncement = createScreenReaderAnnouncement( boxData, attribute, this.showOutliers, attributeNames );

                // Browser-specific accessibility approach
                const isSafari = Dyn.plotUtil.isSafari();

                if( isSafari ) {
                    // Safari: Use optimal approach with short label + live region announcements
                    const accessibleLabel = createAccessibleLabel( attributeNames );
                    ioAdornArray[cellIndex].node.setAttribute( 'aria-label', accessibleLabel );
                } else {
                    // Chrome/Other: Fallback to long aria-label (live regions don't work with VoiceOver in Chrome)
                    // Use the detailed tooltip string so screen readers get full information
                    ioAdornArray[cellIndex].node.setAttribute( 'aria-label', toolTipString );
                }

                // Store detailed stats for keyboard interaction (both browsers)
                ioAdornArray[cellIndex].data( 'detailedStats', toolTipString );
                ioAdornArray[cellIndex].data( 'screenReaderStats', screenReaderAnnouncement );
                ioAdornArray[cellIndex].data( 'isSafari', isSafari );

                addPlotMeasures( this.showOutliers, attribute.id, cell.xBinIndex, cell.yBinIndex );
            }

            // Updated cliping order to resolve boxplot adornment cut off issue on swaping the axis.
            ioAdornArray[cellIndex].attr( 'clip-rect', clipRectString );
            // animate to the new box position, and if using a starting path, increase opacity from 0 to 100%
            ioAdornArray[cellIndex].animate( { 'path': pathString, 'opacity': 1, 'fill': fillColor },
                animationTime, Dyn.plotUtil.stdAnimationEasing ).data( "tooltip", toolTipString ).hover( overScope, outScope );
        }

        function addPlotMeasures( showOutliers, attributeID, xBinIndex, yBinIndex ) {
            // save data for summary table
            if( showOutliers ) {
                plotMeasures.addToMeasuresArray( measuresArray, {
                    measureName: Dyn.loc( "Lower Whisker" ),
                    value: boxW1,
                    attributeID: attributeID
                }, xBinIndex, yBinIndex );
            } else {
                plotMeasures.addToMeasuresArray( measuresArray, {
                    measureName: Dyn.loc( "Minimum" ),
                    value: boxMin,
                    attributeID: attributeID
                }, xBinIndex, yBinIndex );
            }
            plotMeasures.addToMeasuresArray( measuresArray, {
                measureName: Dyn.loc( "Quartile 1" ),
                value: boxQ1,
                attributeID: attributeID
            }, xBinIndex, yBinIndex );
            plotMeasures.addToMeasuresArray( measuresArray, {
                measureName: Dyn.loc( "Median" ),
                value: boxMedian,
                attributeID: attributeID
            }, xBinIndex, yBinIndex );
            plotMeasures.addToMeasuresArray( measuresArray, {
                measureName: Dyn.loc( "Quartile 3" ),
                value: boxQ3,
                attributeID: attributeID
            }, xBinIndex, yBinIndex );
            if( showOutliers ) {
                plotMeasures.addToMeasuresArray( measuresArray, {
                    measureName: Dyn.loc( "Upper Whisker" ),
                    value: boxW2,
                    attributeID: attributeID
                }, xBinIndex, yBinIndex );
            } else {
                plotMeasures.addToMeasuresArray( measuresArray, {
                    measureName: Dyn.loc( "Maximum" ),
                    value: boxMax,
                    attributeID: attributeID
                }, xBinIndex, yBinIndex );
            }
            plotMeasures.addToMeasuresArray( measuresArray, {
                measureName: Dyn.loc( "Inter-Quartile Range" ),
                value: boxIQR,
                attributeID: attributeID
            }, xBinIndex, yBinIndex );
        }

        function createAccessibleLabel( attributeNames ) {
            // Short, descriptive label for aria-label (accessibility best practice)
            const attributeText = attributeNames.length > 1 ?
                attributeNames.join(" and ") :
                attributeNames[0];
            return Dyn.loc( "Boxplot of {{attribute}}. Press Enter for statistics.",
                { attribute: attributeText } );
        }

        function formatBoxplotStats( showOutliers, attributeNames, separator ) {
            // Unified function to format boxplot statistics with configurable separator
            // separator: "\n" for visual tooltip, ", " for screen reader announcement
            const attributeText = attributeNames.length > 1 ? attributeNames.join(" and ") : attributeNames[0];
            const isScreenReader = (separator === ", ");
            const ending = isScreenReader ? "." : "";

            let stats = Dyn.loc( "Boxplot of " + attributeText ) + (isScreenReader ? ". " : "");

            stats += separator + Dyn.loc(
                showOutliers ? "Lower Whisker = {{boxW1}}" : "Minimum = {{boxMin}}",
                showOutliers ? { boxW1: boxW1 } : { boxMin: boxMin }
            );

            stats += separator + Dyn.loc( "Quartile 1 = {{boxQ1}}", { boxQ1: boxQ1 } );
            stats += separator + Dyn.loc( "Median = {{boxMedian}}", { boxMedian: boxMedian } );
            stats += separator + Dyn.loc( "Quartile 3 = {{boxQ3}}", { boxQ3: boxQ3 } );

            stats += separator + Dyn.loc(
                showOutliers ? "Upper Whisker = {{boxW2}}" : "Maximum = {{boxMax}}",
                showOutliers ? { boxW2: boxW2 } : { boxMax: boxMax }
            );

            stats += separator + Dyn.loc( "Inter-Quartile Range = {{boxIQR}}", { boxIQR: boxIQR } ) + ending;

            return stats;
        }

        function boxToolTipString( boxData, attribute, showOutliers, attributeNames ) {
            // Detailed tooltip for visual hover (uses newlines)
            return formatBoxplotStats( showOutliers, attributeNames, "\n" );
        }

        function createScreenReaderAnnouncement( boxData, attribute, showOutliers, attributeNames ) {
            // Format statistics for screen reader with natural pauses (uses commas)
            return formatBoxplotStats( showOutliers, attributeNames, ", " );
        }

        function overScope( event ) {
            thisBox.addGlow( this, Dyn.plotUtil.boxPlotStroke );
            Dyn.toolTip.show( this, event, { x: this.attrs.x, y: this.attrs.y } );
        }

        function outScope( event ) {
            if( Dyn.toolTip.isMyElement( this ) )
                Dyn.toolTip.hide();
            thisBox.removeGlow();
        }
    }

    /**
     *
     * @param valueAxis
     * @param boxData
     * @param cellBounds
     * @returns {*}
     */
    calcBoxPathStringForCell( valueAxis, boxData, cellBounds ) {

        function dataToPixelString( dataValue ) {
            return valueAxis.dataToPixel( dataValue ).toFixed( 1 );
        }

        function lineTo( axisCoord, crossCoord ) {
            if( isHorizontalBox ) {
                return 'L' + axisCoord + ',' + crossCoord;
            } else {
                return 'L' + crossCoord + ',' + axisCoord;
            }
        }

        function moveTo( axisCoord, crossCoord ) {
            if( isHorizontalBox ) {
                return 'M' + axisCoord + ',' + crossCoord;
            } else {
                return 'M' + crossCoord + ',' + axisCoord;
            }
        }

        var pathString;

        if( isFinite( boxData.median ) ) {

            var isHorizontalBox = valueAxis.isHorizontal,
                boxCenter = Math.round( isHorizontalBox ? (cellBounds.y + 0.5 * cellBounds.height) : (cellBounds.x + 0.5 * cellBounds.width) ),
                boxHalfHeight = Math.round( 0.5 * Dyn.plotUtil.boxHeight * (isHorizontalBox ? cellBounds.height : cellBounds.width) ),
                boxMin = dataToPixelString( this.showOutliers ? boxData.whisker1 : boxData.min ),
                boxMax = dataToPixelString( this.showOutliers ? boxData.whisker2 : boxData.max ),
                boxQ1 = dataToPixelString( boxData.q1 ),
                boxQ3 = dataToPixelString( boxData.q3 ),
                boxMedian = dataToPixelString( boxData.median );

            pathString =
                moveTo( boxMin, boxCenter ) + lineTo( boxQ1, boxCenter ) + // line from min to q1
                moveTo( boxQ3, boxCenter ) + lineTo( boxMax, boxCenter ) + // line from q3 to max
                moveTo( boxMedian, boxCenter - boxHalfHeight ) + lineTo( boxMedian, boxCenter + boxHalfHeight ) + // line at median
                moveTo( boxQ1, boxCenter - boxHalfHeight ) + lineTo( boxQ3, boxCenter - boxHalfHeight ) +
                lineTo( boxQ3, boxCenter + boxHalfHeight ) + lineTo( boxQ1, boxCenter + boxHalfHeight ) + 'z'; // box around quartiles
        } else {
            pathString = this.plotShaper.iconStyler.calcEmptyPath();
        }
        return pathString;
    }

    displayOrderBy() {
        return false;
    }

    allowCustomOrderBy() {
        return false;
    }

    /**
     * We are about to add a new plot element to the plot, should the axis customizations be reset?
     * NOTE: this element will be replaced by the new element if both are of type kControlsShape .
     * @param newPlotElement
     * @returns {boolean}
     */
    wantAttributeResetOnAddElement( newPlotElement ) {
        let isShaper = newPlotElement[Dyn.EPlotControl.kControlsShape],
            isBoxPlot = newPlotElement[Dyn.EPlotControl.kControlsBoxOnlyPlot] ||
                newPlotElement[Dyn.EPlotControl.kControlsBoxAndOutlierPlot] ||
                newPlotElement[Dyn.EPlotControl.kControlsBoxAndDotPlot];
        return isShaper && !isBoxPlot; // reset axis if being replaced with a shaper, but not for other boxplots
    }

    /** get the precision to use for this average, using rules for significant figures + user overrides + average overrides */
    getPrecision( attribute ) {
        /*  +1 for statistics that contain one division or multiplication,
            from "Significant Figures - Rules & in Tuva" from Kristian Hunter-Thomson.
            Note that sometimes a median or other value has sig. fig. of +0 if there was no division,
            because the median falls on an exact data value. We would have to record that value by value in the
            calBoxPlotData() and quartilesOfNumericArray() routines.  For now, we keep it simple.
         */
        const significantFiguresDelta = 1;
        return attribute.getNumericDecimals() + significantFiguresDelta + Dyn.preferences.getPrecision();
    }

    getNumCategoriesInLegend( legendAttribute ) {
        return (legendAttribute && legendAttribute.stats.isCategorical() && legendAttribute.stats.categories.length > 1) ? legendAttribute.stats.categories.length : 1;
    }

    getBoxFillColor( colorAttribute, categoryIndex ) {
        if( this.isColoredBoxPlotFill && colorAttribute ) {
            // if legend has categories and boxes have one category each
            Dyn.assert( colorAttribute.stats.isCategorical() );
            Dyn.assertArrayIndex( colorAttribute.stats.categories, categoryIndex, "invalid category index" );
            return Dyn.colorUtil.calcCategoryColor(
                colorAttribute.statsPreFilter,
                colorAttribute.color,
                colorAttribute.stats.categories[categoryIndex],
                false,
                colorAttribute.colorMap,
                colorAttribute.isSortAscending ).colorString;
        } else {
            return Dyn.plotUtil.boxPlotFill; // gray fill
        }
    }

    /** override replaces() to preserve old boxplot shapes to animate into new boxplot positions (by calcAdornments()) */
    moveOldBoxPlotAdornments( iExistingPlotElement, ioPlotAdornments ) {
        switch( iExistingPlotElement.primaryType ) {
            case Dyn.EPlotControl.kControlsBoxOnlyPlot:
            case Dyn.EPlotControl.kControlsBoxAndOutlierPlot:
            case Dyn.EPlotControl.kControlsBoxAndDotPlot:
                if( (iExistingPlotElement.primaryType !== this.primaryType) ) {
                    ioPlotAdornments.renameAdornmentsArray( iExistingPlotElement.primaryType, this.primaryType );
                }
                break;
            // else do nothing
        }
    }
}
Dyn.BoxPlotElement = BoxPlotElement;

/**
 * Dyn.BoxOnlyPlotElement()
 *
 * Box plot that shows outliers.
 * Outliers are cases less than lower quartile - 1.5 IQR,
 * or greater than upper quartile + 1.5 IQR, if any.
 */
class BoxOnlyPlotElement extends BoxPlotElement {

    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityShape,
            replaceType: Dyn.EPlotControl.kControlsShape,
            primaryType: Dyn.EPlotControl.kControlsBoxOnlyPlot,
            otherControls: []
        }, Dyn );

        // limit case stacking area to within the box (1==entire cell, 0.5=half of cell height, etc.)
        //this.stackHeight = Dyn.plotUtil.boxHeight;
        //this.showOutliers = false;
        //this.showDots = false;
        //this.needsDefaultTitle = true;
    }

    get kDefaultTitle() { return Dyn.plotTitles.BOX_PLOT; }
    get kUndoAction()   { return Dyn.plotTitles.BOX_PLOT; }

    calcPosition( ioPositions, xAxisView, yAxisView, ioAdornments ) {
        if( !(this.isBoxOnX || this.isBoxOnY) ) {
            // position cases just like dot plot and scatterplot
            super.calcPosition( ioPositions, xAxisView, yAxisView, ioAdornments ); // DotPlotBase.calcPosition();
            return;
        }
        // else position cases and boxes and outliers
        this.calcAllBoxPositions( ioPositions, xAxisView, yAxisView, ioAdornments );
    }

    replaces( iExistingPlotElement, ioPlotAdornments ) {
        // extend replaces() to preserve old boxplot shapes so calcAdornments() can animate them into new positions.
        this.moveOldBoxPlotAdornments( iExistingPlotElement, ioPlotAdornments );
        return super.replaces( iExistingPlotElement, ioPlotAdornments );
    }

} // class BoxOnlyPlotElement

Dyn.BoxOnlyPlotElement = BoxOnlyPlotElement;
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsBoxOnlyPlot, Dyn.BoxOnlyPlotElement );


/**
 * Dyn.BoxAndOutlierPlotElement()
 *
 * Box plot that shows outliers.
 * Outliers are cases less than lower quartile - 1.5 IQR,
 * or greater than upper quartile + 1.5 IQR, if any.
 */
class BoxAndOutlierPlotElement extends BoxPlotElement {

    constructor( Dyn ) {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityShape,
            replaceType: Dyn.EPlotControl.kControlsShape,
            primaryType: Dyn.EPlotControl.kControlsBoxAndOutlierPlot,
            otherControls: []
        }, Dyn );

        // limit case stacking area to within the box (1==entire cell, 0.5=half of cell height, etc.)
        //this.stackHeight = Dyn.plotUtil.boxHeight;
        this.showOutliers = true;
        this.showDots = false;
        //this.needsDefaultTitle = true;
    }

    get kDefaultTitle() { return Dyn.plotTitles.BOX_PLOT_WITH_OUTLIERS; }
    get kUndoAction()   { return Dyn.plotTitles.BOX_PLOT_WITH_OUTLIERS; }

    calcPosition( ioPositions, xAxisView, yAxisView, ioAdornments ) {
        "use strict";
        if ( !(this.isBoxOnX || this.isBoxOnY ) ) {
            // position cases just like dot plot and scatterplot
            super.calcPosition( ioPositions, xAxisView, yAxisView, ioAdornments ); // DotPlotBase.calcPosition();
            return;
        }
        // else position cases and boxes and outliers
        this.calcAllBoxPositions( ioPositions, xAxisView, yAxisView, ioAdornments );
    }

    replaces( iExistingPlotElement, ioPlotAdornments ) {
        // extend replaces() to preserve old boxplot shapes so calcAdornments() can animate them into new positions.
        this.moveOldBoxPlotAdornments( iExistingPlotElement, ioPlotAdornments );
        return super.replaces( iExistingPlotElement, ioPlotAdornments );
    }

} // class BoxAndOutlierPlotElement

Dyn.BoxAndOutlierPlotElement = BoxAndOutlierPlotElement;
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsBoxAndOutlierPlot, Dyn.BoxAndOutlierPlotElement );

/**
 * Dyn.BoxAndDotPlotElement()
 */
class BoxAndDotPlotElement extends BoxPlotElement {

    constructor() {
        super( Dyn );
        this.initElement( {
            priority: Dyn.EPlotPriority.kPriorityShape,
            replaceType: Dyn.EPlotControl.kControlsShape,
            primaryType: Dyn.EPlotControl.kControlsBoxAndDotPlot,
            otherControls: []
        }, Dyn );

        // limit case stacking area to within the box (1==entire cell, 0.5=half of cell height, etc.)
        //this.stackHeight = Dyn.plotUtil.boxHeight;
        this.showOutliers = false;
        this.showDots = true;
        //this.needsDefaultTitle = true;
    }

    get kDefaultTitle() {
        return Dyn.plotTitles.BOX_DOT_PLOT;
    }

    get kUndoAction() {
        return Dyn.plotTitles.BOX_DOT_PLOT;
    }

    calcPosition( ioPositions, xAxisView, yAxisView, ioAdornments ) {
        if ( !(this.isBoxOnX || this.isBoxOnY ) ) {
            // position cases just like dot plot and scatterplot
            super.calcPosition( ioPositions, xAxisView, yAxisView, ioAdornments ); // DotPlotBase.calcPosition();
            return;
        }
        // else position cases and boxes and outliers
        this.calcAllBoxPositions( ioPositions, xAxisView, yAxisView, ioAdornments );
    };

    displayOrderBy() {
        // true for Box and Dot, because dots can be stacked
        return true;
    };

    allowCustomOrderBy() {
        // true for Box and Dot, because dots can be stacked
        return true;
    }

    replaces( iExistingPlotElement, ioPlotAdornments ) {
        // extend replaces() to preserve old boxplot shapes so calcAdornments() can animate them into new positions.
        this.moveOldBoxPlotAdornments( iExistingPlotElement, ioPlotAdornments );
        return super.replaces( iExistingPlotElement, ioPlotAdornments );
    }

} // class BoxAndDotPlotElement

Dyn.BoxAndDotPlotElement = BoxAndDotPlotElement;
Dyn.PlotElemHelper.registerOldType( Dyn.EPlotControl.kControlsBoxPlot, (savedData) => Dyn.BoxAndDotPlotElement ); // old box plot creates Box and Dot
Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsBoxAndDotPlot, Dyn.BoxAndDotPlotElement );

} // wrapDynBoxPlotElement
